import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Phone } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useLang } from "../context/LanguageContext";
import { T } from "../i18n/translations";
import { MENU } from "../data/menu";
import { MEDIA, CONTACT } from "../data/media";

const CAT_IMG = {
  pranzo: [MEDIA.lunchBoard, MEDIA.lunchPolpette, MEDIA.lunchOrecchiette, MEDIA.lunchSalmon],
  starter: MEDIA.bruschetta,
  tartare: MEDIA.vealTartare,
  mains: MEDIA.octopus,
  salads: MEDIA.tunaTartare,
  dessert: MEDIA.dessert,
};

const MenuRow = ({ item, lang }) => {
  const hasDesc = item[`desc_${lang}`];
  return (
    <div className="py-5 border-b border-dotted border-trise-brass/50" data-testid="menu-row">
      <div className="flex items-baseline">
        <h4 className="text-lg md:text-xl font-bold text-trise-ink pr-2">{item[lang]}</h4>
        {item.price && <span className="dotted-leader" />}
        {item.price && <span className="text-lg font-bold text-trise-blue shrink-0">{item.price}</span>}
      </div>
      {hasDesc && (
        <p className="mt-1 text-sm text-trise-ink/70 leading-relaxed max-w-xl">{item[`desc_${lang}`]}</p>
      )}
      {item.allergens && (
        <span className="mt-2 inline-block text-[0.65rem] uppercase tracking-widen text-trise-brass">
          {lang === "it" ? "Allergeni" : "Allergens"} {item.allergens}
        </span>
      )}
    </div>
  );
};

export default function MenuPage() {
  const { lang } = useLang();
  return (
    <>
      {/* Header */}
      <section className="relative bg-trise-blue text-trise-ivory pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden grain" data-testid="menu-header">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Reveal delay={0.05}>
            <h1 className="text-6xl md:text-8xl font-bold tracking-tight">{T.menu_page.heading[lang]}</h1>
          </Reveal>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-trise-ivory py-20 md:py-28" data-testid="menu-body">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col gap-20 md:gap-28">
          {MENU.map((cat) => (
            <React.Fragment key={cat.id}>
              {cat.id === "starter" && (
                <div className="pt-4 border-t-2 border-trise-brass/60 scroll-mt-28" id="cena" data-testid="menu-cena-heading">
                  <Reveal>
                    <p className="text-xs uppercase tracking-widen text-trise-terra font-bold">
                      {lang === "it" ? "La sera" : "In the evening"}
                    </p>
                    <h2 className="mt-2 text-4xl md:text-6xl font-bold text-trise-blue">
                      {lang === "it" ? "Menu cena" : "Dinner menu"}
                    </h2>
                  </Reveal>
                </div>
              )}
              <div id={cat.id} className="grid md:grid-cols-12 gap-8 md:gap-14 scroll-mt-28" data-testid={`menu-cat-${cat.id}`}>
              <div className="md:col-span-4">
                <div className="md:sticky md:top-28">
                  <h2 className="text-3xl md:text-4xl font-bold text-trise-ink">{cat[lang]}</h2>
                  {cat.blurb && (
                    <div className="mt-4 space-y-3 text-base md:text-lg text-trise-ink/80 leading-relaxed">
                      {cat.blurb[lang].map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                  )}
                  {CAT_IMG[cat.id] && (
                    <div className="hidden md:block mt-6 space-y-6">
                      {(Array.isArray(CAT_IMG[cat.id]) ? CAT_IMG[cat.id] : [CAT_IMG[cat.id]]).map((src, i) => (
                        <img
                          key={i}
                          src={src}
                          alt={`${cat[lang]} — ${i + 1}`}
                          loading="lazy"
                          className="w-full aspect-[4/5] object-cover border border-trise-brass/40"
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="md:col-span-8">
                {cat.items.map((item, i) =>
                  item.sub ? (
                    <Reveal key={i} delay={Math.min(i * 0.04, 0.2)} y={20}>
                      <h3 className="mt-8 first:mt-0 mb-2 pb-2 border-b border-trise-brass/40 text-sm uppercase tracking-widen text-trise-terra font-bold">
                        {item[lang]}
                        {item.range && <span className="text-trise-ink/50 font-normal ml-2">· {item.range}</span>}
                      </h3>
                    </Reveal>
                  ) : (
                    <Reveal key={i} delay={Math.min(i * 0.04, 0.2)} y={24}>
                      <MenuRow item={item} lang={lang} />
                    </Reveal>
                  )
                )}
              </div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <p className="mt-16 text-center text-xs uppercase tracking-widen text-trise-ink/50" data-testid="coperto-note">
          {lang === "it" ? "Coperto € 2,00" : "Cover charge € 2.00"}
        </p>
        <p className="mt-3 max-w-2xl mx-auto text-center text-xs text-trise-ink/45 leading-relaxed" data-testid="frozen-note">
          {lang === "it"
            ? "* In caso di mancanza del prodotto fresco, sarà usato un prodotto surgelato di equivalente qualità."
            : "* If fresh product is unavailable, a frozen product of equivalent quality will be used."}
        </p>
      </section>

      {/* Reserve strip */}
      <section className="bg-trise-white py-20 md:py-28" data-testid="menu-reserve">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <img src={MEDIA.social} alt="Esperienza Trisé" loading="lazy" className="w-full aspect-[16/10] object-cover border border-trise-brass/40" />
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.05}>
              <p className="text-2xl md:text-3xl font-light text-trise-ink leading-snug">{T.menu_page.reserveNote[lang]}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="menu-whatsapp" className="inline-flex items-center gap-2 bg-trise-blue text-trise-ivory px-8 py-4 text-xs uppercase tracking-widen font-bold hover:bg-trise-terra transition-colors duration-300">
                  <MessageCircle size={16} /> WhatsApp
                </a>
                <a href={`tel:${CONTACT.phoneTel}`} data-testid="menu-call" className="inline-flex items-center gap-2 border border-trise-brass text-trise-ink px-8 py-4 text-xs uppercase tracking-widen font-bold hover:bg-trise-brass hover:text-trise-blue transition-colors duration-300">
                  <Phone size={16} /> {CONTACT.phoneDisplay}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
